import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, Tag, ArrowLeft, ArrowRight } from 'lucide-react';
import { blogPosts, categories } from '../data/mock';

const Category = () => {
  const { slug } = useParams();
  const category = categories.find(c => c.slug === slug);
  const categoryPosts = blogPosts
    .filter(post => post.categorySlug === slug)
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

  if (!category) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Category not found</h1>
        <Link to="/" className="text-gray-600 hover:text-gray-900 flex items-center justify-center space-x-2">
          <ArrowLeft size={16} />
            <span>Back</span>
        </Link>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Navigation */}
      <nav className="mb-8">
        <Link 
          to="/" 
          className="text-gray-600 hover:text-gray-900 flex items-center space-x-2 transition-colors"
        >
          <ArrowLeft size={16} />
            <span>Back</span>
        </Link>
      </nav>

      {/* Category Header */}
      <header className="mb-12">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">{category.name}</h1>
          <p className="text-gray-600 mb-4">
            {category.count} {category.count === 1 ? 'post' : 'posts'} in this category
          </p>
          
          {/* Category Description */}
          <div className="max-w-2xl mx-auto">
            {slug === 'investment-memos' && (
              <p className="text-gray-600 leading-relaxed">
                Deep-dive analysis of specific companies, markets, and investment opportunities. These memos cover due diligence insights, market positioning, and investment thesis development.
              </p>
            )}
            {slug === 'market-analysis' && (
              <p className="text-gray-600 leading-relaxed">
                Comprehensive market research and trend analysis across various sectors. Focus on understanding market dynamics, competitive landscapes, and emerging opportunities.
              </p>
            )}
            {slug === 'startup-thesis' && (
              <p className="text-gray-600 leading-relaxed">
                Strategic frameworks and investment thesis development for startup ecosystems. Exploring patterns, opportunities, and key success factors in early-stage investing.
              </p>
            )}
            {slug === 'industry-reports' && (
              <p className="text-gray-600 leading-relaxed">
                Sector-specific reports covering industry trends, regulatory changes, and market evolution. Data-driven insights for understanding industry dynamics.
              </p>
            )}
            {slug === 'ecosystem-insights' && (
              <p className="text-gray-600 leading-relaxed">
                Analysis of startup ecosystems, funding patterns, and regional market dynamics. Understanding how different geographies and communities build sustainable innovation environments.
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Category Stats */}
      <section className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{categoryPosts.length}</div>
            <div className="text-sm text-gray-600">Total Posts</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {categoryPosts.reduce((acc, post) => acc + parseInt(post.readTime), 0)}
            </div>
            <div className="text-sm text-gray-600">Total Read Time (min)</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {[...new Set(categoryPosts.flatMap(post => post.tags))].length}
            </div>
            <div className="text-sm text-gray-600">Unique Tags</div>
          </div>
        </div>
      </section>

      {/* Posts List */}
      <section>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Posts</h2>
          <div className="text-sm text-gray-500">
            Sorted by newest first
          </div>
        </div>

        {categoryPosts.length > 0 ? (
          <div className="space-y-6">
            {categoryPosts.map((post) => (
              <article key={post.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                  <div className="flex items-center space-x-1">
                    <Calendar size={14} />
                    <span>{formatDate(post.publishedAt)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock size={14} />
                    <span>{post.readTime}</span>
                  </div>
                  {post.featured && (
                    <span className="bg-gray-900 text-white px-2 py-1 rounded text-xs font-medium">
                      Featured
                    </span>
                  )}
                </div>
                
                <Link to={`/post/${post.slug}`} className="group">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-gray-700 transition-colors mb-3">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                </Link>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-wrap gap-2">
                    {post.tags.slice(0, 4).map((tag) => (
                      <span key={tag} className="text-xs text-gray-500 flex items-center">
                        <Tag size={12} className="mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                  <Link 
                    to={`/post/${post.slug}`}
                    className="text-sm text-gray-900 font-medium hover:text-gray-700 transition-colors flex items-center space-x-1"
                  >
                    <span>Read</span>
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No posts found in this category.</p>
            <Link 
              to="/"
              className="text-gray-900 hover:text-gray-700 font-medium transition-colors"
            >
              Browse all posts
            </Link>
          </div>
        )}
      </section>

      {/* Related Categories */}
      <section className="mt-16">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Other Categories</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories
            .filter(c => c.slug !== slug)
            .map((relatedCategory) => (
              <Link
                key={relatedCategory.id}
                to={`/category/${relatedCategory.slug}`}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow group"
              >
                <h4 className="font-semibold text-gray-900 group-hover:text-gray-700 transition-colors mb-2">
                  {relatedCategory.name}
                </h4>
                <p className="text-sm text-gray-600">
                  {relatedCategory.count} {relatedCategory.count === 1 ? 'post' : 'posts'}
                </p>
              </Link>
            ))}
        </div>
      </section>
    </div>
  );
};

export default Category;